import React, {useEffect, useState} from 'react';
import {Observer} from 'mobx-react-lite';
import DocumentSettingsTabStore from './DocumentSettingsTabStore';
import DocumentIssuerMode from '../../../../entities/DocumentIssuerMode';
import {useStyles} from './DocumentSettingsTab.style';
import {useCommonContext} from '../../../../contexts/CommonContext';
import {useI18nContext} from '../../../../contexts/I18nContext';
import TextInput from '../../../../ui_components/TextInput/TextInput';
import Textarea from '../../../../ui_components/Textarea/Textarea';
import Button from '../../../../ui_components/Button/Button';
import ButtonType from '../../../../ui_components/Button/ButtonType';
import clsx from 'clsx';

const MODES: DocumentIssuerMode[] = [
    DocumentIssuerMode.AGENCY,
    DocumentIssuerMode.CUSTOM,
    DocumentIssuerMode.HIDDEN,
];

const MODE_KEYS: Record<DocumentIssuerMode, {label: string; description: string}> = {
    [DocumentIssuerMode.AGENCY]: {
        label: 'DOCUMENT_ISSUER_MODE_AGENCY',
        description: 'DOCUMENT_ISSUER_MODE_AGENCY_DESCRIPTION',
    },
    [DocumentIssuerMode.CUSTOM]: {
        label: 'DOCUMENT_ISSUER_MODE_CUSTOM',
        description: 'DOCUMENT_ISSUER_MODE_CUSTOM_DESCRIPTION',
    },
    [DocumentIssuerMode.HIDDEN]: {
        label: 'DOCUMENT_ISSUER_MODE_HIDDEN',
        description: 'DOCUMENT_ISSUER_MODE_HIDDEN_DESCRIPTION',
    },
};

export interface DocumentSettingsTabProperties {
    /** Agency being configured; omitted means the logged user's own agency. */
    agencyId?: string;
}

const DocumentSettingsTab: React.FC<DocumentSettingsTabProperties> = ({agencyId}) => {
    const classes = useStyles();
    const commonStore = useCommonContext();
    const i18n = useI18nContext();
    const [store] = useState(() => new DocumentSettingsTabStore(commonStore, agencyId));

    useEffect(() => {
        store.load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const nonEmpty = (value?: string) => {
        const trimmed = value?.trim();
        return trimmed ? trimmed : undefined;
    };

    return (
        <Observer>{() => {
            const settings = store.settings;
            const isCustom = settings.mode === DocumentIssuerMode.CUSTOM;

            // Mirrors what agency-core prints; the AGENCY mode is described instead of rendered
            // because the agency's own contact data is not part of this screen's payload.
            const issuer = isCustom
                ? {
                    name: nonEmpty(settings.name),
                    phone: nonEmpty(settings.phone),
                    address: nonEmpty(settings.address),
                    email: nonEmpty(settings.email),
                }
                : {};
            const footerNote = nonEmpty(settings.footerNote);
            const hasPreviewContent = !!(issuer.name || issuer.phone || issuer.address || issuer.email || footerNote);

            return (
                <div className={classes.container}>
                    <div className={classes.description}>
                        {i18n.translate("DOCUMENT_SETTINGS_DESCRIPTION")}
                    </div>

                    <div className={classes.modes_container}>
                        {MODES.map(mode => (
                            <button
                                key={mode}
                                type="button"
                                disabled={store.isLoading || store.isSaving}
                                onClick={() => store.setMode(mode)}
                                className={clsx(
                                    classes.mode_card,
                                    settings.mode === mode && classes.mode_card_selected
                                )}>
                                <div className={classes.mode_label}>{i18n.translate(MODE_KEYS[mode].label)}</div>
                                <div className={classes.mode_description}>
                                    {i18n.translate(MODE_KEYS[mode].description)}
                                </div>
                            </button>
                        ))}
                    </div>

                    {isCustom && (
                        <>
                            <div className={classes.section_title}>{i18n.translate("DOCUMENT_ISSUER_DATA")}</div>
                            <div className={classes.fields_container}>
                                <TextInput label={i18n.translate("NAME")}
                                           value={settings.name || ''}
                                           fullWidth
                                           onChange={(e) => store.setField("name", e.target.value)}/>
                                <TextInput label={i18n.translate("PHONE")}
                                           value={settings.phone || ''}
                                           fullWidth
                                           onChange={(e) => store.setField("phone", e.target.value)}/>
                                <TextInput label={i18n.translate("ADDRESS")}
                                           value={settings.address || ''}
                                           fullWidth
                                           onChange={(e) => store.setField("address", e.target.value)}/>
                                <TextInput label={i18n.translate("EMAIL")}
                                           value={settings.email || ''}
                                           fullWidth
                                           onChange={(e) => store.setField("email", e.target.value)}/>
                            </div>
                        </>
                    )}

                    <Textarea label={i18n.translate("DOCUMENT_FOOTER_NOTE")}
                              placeholder={i18n.translate("DOCUMENT_FOOTER_NOTE_PLACEHOLDER")}
                              value={settings.footerNote || ''}
                              lines={2}
                              fullWidth
                              onChange={(e) => store.setField("footerNote", e.target.value)}/>

                    <div className={classes.section_title}>{i18n.translate("DOCUMENT_ISSUER_PREVIEW")}</div>
                    <div className={classes.preview_container}>
                        {settings.mode === DocumentIssuerMode.AGENCY ? (
                            <div className={classes.description}>
                                {i18n.translate("DOCUMENT_ISSUER_MODE_AGENCY_DESCRIPTION")}
                                {footerNote && <div className={classes.preview_line}>{footerNote}</div>}
                            </div>
                        ) : hasPreviewContent ? (
                            <>
                                {issuer.name && <div className={classes.preview_name}>{issuer.name}</div>}
                                {issuer.phone && <div className={classes.preview_line}>{issuer.phone}</div>}
                                {issuer.address && <div className={classes.preview_line}>{issuer.address}</div>}
                                {issuer.email && <div className={classes.preview_line}>{issuer.email}</div>}
                                {footerNote && <div className={classes.preview_line}>{footerNote}</div>}
                            </>
                        ) : (
                            <div className={classes.preview_empty}>
                                {i18n.translate("DOCUMENT_ISSUER_PREVIEW_EMPTY")}
                            </div>
                        )}
                    </div>

                    <div className={classes.actions_container}>
                        <Button text={i18n.translate("SAVE")}
                                type={ButtonType.PRIMARY}
                                loading={store.isSaving}
                                disabled={store.isLoading || store.isSaving}
                                onClick={() => store.save()}/>
                    </div>
                </div>
            );
        }}
        </Observer>
    );
};

export default DocumentSettingsTab;
